import * as React from 'react';
import { useDispatch } from 'react-redux';
import { isTablet } from '@westwing/ui-kit/breakPoints';
import { useCookies } from 'react-cookie';
import { OverlayId } from 'Client/components/Overlays/types';
import { hideOverlay, showOverlay } from 'Client/redux/header/overlays/actions';
import useShowPromoBarBanner from 'Client/hooks/flags/useShowPromoBarBanner';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import useUserTracking from 'Client/hooks/flags/useUserTracking';
import { PageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { ProductListingPages } from 'Client/hooks/UrlManager/types';
import useRouter from 'Client/hooks/useRouter';
import { getPageTypeByUrl } from 'Client/hooks/UrlManager/pageTypeService';
import useGa from 'Client/hooks/useGa';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { appBannerUrl } from 'Common/helpers/app';
import { TrackAppBannerActions } from 'Common/ga/types';
import { getOverlayById } from 'Client/redux/header/overlays/selectors';
import useDeviceType from 'Client/hooks/useDeviceType';
import { AppAdjustTracker, AppBannerValues } from 'Common/appshell_types';
import { useBellaSelector } from 'Client/redux/hooks';
import useBellaCookies, { oneDayTTL } from 'Client/hooks/useBellaCookies';
import { SENT_BRAZE_READY_EVENT } from 'Client/hooks/Braze/useBrazeReady';
import { SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION } from 'Client/components/Overlays/ShopToClub/hooks/useClubRegistrationPopup';
import theme from 'Client/components/GlobalStyles/theme';
import isClient from 'Common/helpers/environment';
import useConfig from 'Client/hooks/useConfig';

export { AppBannerValues } from 'Common/appshell_types';
const APP_BANNER_DISMISSED = 'ww_app_banner_dismissed';
export const SMALL_APP_BANNER_SHOWN_IN_SESSION = 'ww_small_app_banner_shown_in_session';

type PerAppBannerValue<ValType> = {
    [key in AppBannerValues]: ValType;
};

const pageWhiteLists: PerAppBannerValue<Array<PageType>> = {
    smart: [...ProductListingPages, PageType.HOMEPAGE, PageType.INTERIOR_DESIGN_SERVICE, PageType.LLP, PageType.LOOKS],
    small: [...ProductListingPages, PageType.HOMEPAGE],
    micro: [...ProductListingPages, PageType.HOMEPAGE, PageType.INTERIOR_DESIGN_SERVICE, PageType.LLP, PageType.LOOKS],
    none: [],
};

export const useP100AppBannerWithDefaultValue = () => {
    const { p100AppBanner } = useFeatureToggle();
    return (p100AppBanner as AppBannerValues) || AppBannerValues.none;
};

export const isCurrentPageIsInWhiteList = (id: string, currentPageType: PageType) => {
    return pageWhiteLists[id].includes(currentPageType);
};

// eslint-disable-next-line complexity
export const parseAppBannerCookie = (
    id: AppBannerValues,
    cookies: {
        [x: string]: any;
    }
): { nextCookie?: string; frequencyCappingIndicatesShow: boolean } => {
    const cookie: string = (cookies && cookies[APP_BANNER_DISMISSED]) || '';
    const [viewCountStr, showAgainTimestampStr] = cookie.split(',');
    const viewCount = Number(viewCountStr);
    const showAgainTimestamp = Number(showAgainTimestampStr);
    const currentTimestamp = Date.now();
    const oneHourInTheFuture = currentTimestamp + 3600000;
    const threeDaysInTheFuture = currentTimestamp + 86400000 * 3;
    const thirtyDaysInTheFuture = currentTimestamp + 86400000 * 30;

    // first time seeing the banner
    if ([NaN, 0].includes(viewCount) || [NaN, 0].includes(showAgainTimestamp)) {
        const futureTimeStamp = id === AppBannerValues.micro ? oneHourInTheFuture : threeDaysInTheFuture;
        return {
            nextCookie: `1,${futureTimeStamp}`,
            frequencyCappingIndicatesShow: true,
        };
    }

    // second time seeing the banner
    if (viewCount === 1 && showAgainTimestamp < currentTimestamp) {
        const futureTimeStamp = id === AppBannerValues.micro ? oneHourInTheFuture : threeDaysInTheFuture;
        return {
            nextCookie: `${viewCount + 1},${futureTimeStamp}`,
            frequencyCappingIndicatesShow: true,
        };
    }

    // third+ time seeing banner
    if (showAgainTimestamp < currentTimestamp) {
        const futureTimeStamp = id === AppBannerValues.micro ? oneHourInTheFuture : thirtyDaysInTheFuture;
        return {
            nextCookie: `${viewCount + 1},${futureTimeStamp}`,
            frequencyCappingIndicatesShow: true,
        };
    }

    return {
        frequencyCappingIndicatesShow: false,
    };
};

export const isBrazeReadyEventBlocking = (id: AppBannerValues, sentBrazeReadyEvent: boolean) =>
    id === AppBannerValues.small ? sentBrazeReadyEvent : false;

// every branch is covered in unit test
// eslint-disable-next-line complexity
export const shouldShowAppBanner = ({
    frequencyCappingIndicatesShow,
    currentPageIsInWhiteList,
    correctAppBannerFeatureToggleValue,
    showClubHeader,
    isLoggedIn,
    isClient,
    isTablet,
    isDoiOverlayActive,
    isBurgerMenuActive,
    isSearchFlyoutActive,
    isBrazeReadyEventBlocking,
    isShopToClubBlocking,
}: {
    frequencyCappingIndicatesShow: boolean;
    currentPageIsInWhiteList: boolean;
    correctAppBannerFeatureToggleValue: boolean;
    showClubHeader: boolean;
    isLoggedIn: boolean;
    isClient: boolean;
    isTablet: boolean;
    isDoiOverlayActive: boolean;
    isBurgerMenuActive: boolean;
    isSearchFlyoutActive: boolean;
    isBrazeReadyEventBlocking: boolean;
    isShopToClubBlocking: boolean;
}) => {
    return !!(
        frequencyCappingIndicatesShow && // hide if shown recently
        currentPageIsInWhiteList &&
        correctAppBannerFeatureToggleValue &&
        !showClubHeader && // don't collide with shop to club overlay
        isLoggedIn &&
        isClient && // the banner should only ever render client side because of layout issues
        !isTablet && // isTablet() is true on screen sizes over mobile so if it's true we should not show the app banner
        !isDoiOverlayActive && // this would collide
        !isBurgerMenuActive && // this would collide
        !isSearchFlyoutActive && // this would collide
        !isBrazeReadyEventBlocking && // show less overlays
        !isShopToClubBlocking
    );
};

export const useShowAppBanner = (id: AppBannerValues) => {
    const { p100AppBanner } = useFeatureToggle();
    const showClubHeader = useShowPromoBarBanner();
    const { isLoggedIn } = useUserTracking();
    const [cookies] = useCookies([APP_BANNER_DISMISSED]);
    const [shopToClubOverlayShownInThisSession] = useBellaCookies(SHOP_TO_CLUB_OVERLAY_SHOWN_IN_THIS_SESSION, true);
    const [sentBrazeReadyEvent] = useBellaCookies(SENT_BRAZE_READY_EVENT, true);
    const { frequencyCappingIndicatesShow } = parseAppBannerCookie(id, cookies);
    const overlays = useBellaSelector(state => state.header.overlays);
    const isDoiOverlayActive = getOverlayById(overlays, OverlayId.DOUBLE_OPT_IN_OVERLAY).isActive;
    const isBurgerMenuActive = getOverlayById(overlays, OverlayId.BURGER_MENU).isActive;
    const isSearchFlyoutActive = getOverlayById(overlays, OverlayId.SEARCH_FLYOUT).isActive;

    const {
        location: { pathname },
        host,
    } = useRouter();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);

    const shouldShow = shouldShowAppBanner({
        frequencyCappingIndicatesShow,
        currentPageIsInWhiteList: isCurrentPageIsInWhiteList(id, currentPageType),
        correctAppBannerFeatureToggleValue: p100AppBanner === id,
        showClubHeader: !!showClubHeader,
        isLoggedIn: !!isLoggedIn,
        isClient: isClient(),
        isTablet: isTablet(),
        isDoiOverlayActive,
        isBurgerMenuActive,
        isSearchFlyoutActive,
        isBrazeReadyEventBlocking: isBrazeReadyEventBlocking(id, sentBrazeReadyEvent),
        isShopToClubBlocking: shopToClubOverlayShownInThisSession,
    });

    return shouldShow;
};

export const useActionsAppBanner = (id: AppBannerValues) => {
    const { appAdjustTrackers } = useConfig();
    const trackers: PerAppBannerValue<AppAdjustTracker> = {
        ...appAdjustTrackers,
        none: { ios: '', android: '' },
    };
    const { p100AppBanner } = useFeatureToggle();
    const [cookies, setCookie] = useCookies([APP_BANNER_DISMISSED]);
    const [, setSmallAppBannerShownInThisSession] = useBellaCookies(SMALL_APP_BANNER_SHOWN_IN_SESSION, true);
    const {
        location: { pathname },
        host,
    } = useRouter();
    const bellaPush = useBellaPush();
    const gaTracking = useGa();
    const dispatch = useDispatch();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);
    const { getByUserAgent } = useDeviceType();
    const shouldShow = useShowAppBanner(id);

    const { nextCookie } = parseAppBannerCookie(id, cookies);
    const setDismissed = () => {
        const cookieData = {
            path: '/',
            expires: new Date(Date.now() + 86400000 * 180), // 180 days because timestamps in cookie value determin frequency capping not expire date
        };
        setCookie(APP_BANNER_DISMISSED, nextCookie, cookieData);
    };

    const onBackdropDismiss = () => {
        if (id !== AppBannerValues.small) {
            return;
        }
        dispatch(hideOverlay(OverlayId.APP_PUSH_SMALL));
        gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerCloseBackground, currentPageType);
        setDismissed();
    };

    const trackCloseFns: PerAppBannerValue<() => void> = {
        small: () => gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerClose, currentPageType),
        smart: () => gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmartBannerClose, currentPageType),
        micro: () => gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.MicroBannerClose, currentPageType),
        none: () => null,
    };

    const close = () => {
        if (id === AppBannerValues.small) {
            dispatch(hideOverlay(OverlayId.APP_PUSH_SMALL));
        }
        trackCloseFns[id]();
        setDismissed();
    };

    const trackClickFns: PerAppBannerValue<() => void> = {
        small: () => gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerClick, currentPageType),
        smart: () => gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmartBannerClick, currentPageType),
        micro: () => gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.MicroBannerClick, currentPageType),
        none: () => null,
    };

    const openInApp = () => {
        trackClickFns[id]();
        bellaPush(appBannerUrl(host, trackers[id], getByUserAgent()));
    };

    const trackImpessionFns: PerAppBannerValue<() => void> = {
        small: () =>
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerImpression, currentPageType),
        smart: () => gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmartBannerImpression, currentPageType),
        micro: () => gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.MicroBannerImpression, currentPageType),
        none: () => null,
    };

    // impression tracking
    React.useEffect(() => {
        if (shouldShow) {
            gaTracking.trackVariation('p100AppBanner', p100AppBanner);
            trackImpessionFns[id]();
            if (id === AppBannerValues.small) {
                dispatch(showOverlay(OverlayId.APP_PUSH_SMALL));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    // viewed small app banner
    React.useEffect(() => {
        if (shouldShow && id === AppBannerValues.small) {
            setSmallAppBannerShownInThisSession(1, oneDayTTL);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    return {
        close,
        openInApp,
        onBackdropDismiss,
    };
};

export const appBannerHeight = ({ shouldShow, bannerId }: { shouldShow: boolean; bannerId: AppBannerValues }) => {
    if (shouldShow && bannerId === AppBannerValues.smart) {
        return theme.smartBannerHeight;
    }
    if (shouldShow && bannerId === AppBannerValues.micro) {
        return theme.microBannerHeight;
    }
    return 0;
};

export const useAppBannerHeight = () => {
    const p100AppBanner = useP100AppBannerWithDefaultValue();
    const shouldShow = useShowAppBanner(p100AppBanner);
    const [height, setHeight] = React.useState(0);
    React.useEffect(() => {
        setHeight(appBannerHeight({ shouldShow, bannerId: p100AppBanner }));
    }, [p100AppBanner, shouldShow]);
    return height;
};
